import { useCallback, useMemo } from 'react';
import Cache from 'timed-cache';

import { logException } from '#/features/logging/logging';
import * as OrderFn from '#/features/perpetuals/trade-overview/order-functions';
import { usePerpetualMarketsState } from '#/features/perpetuals/market-info/markets-context';
import { useWalletView } from '#/features/wallet/wallet-context';

import { useApi } from '#/api/fetch-api';
import { getOrder, Order } from '#/api/orders';
import { FillNotification, OrderNotification } from '#/api/ws/ws';

import useWSListener from '#/utils/useWSListener';
import ms from '#/utils/ms';

import { getOrderNotificationType } from './functions';
import { publishNotification } from './topic';

export function useTradeNotifications() {
  useOrdersListener();
  useFillsListener();
}

function useOrdersListener(): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;
  const channel = 'orders.ALL';

  const { markets } = usePerpetualMarketsState();

  const handleWSNotification = useCallback(
    (notif: OrderNotification) => {
      const order = notif.data;
      const market = markets.entities[order.market];

      if (markets.ids.length === 0) return; // markets not loaded yet

      if (market == null) {
        const message = `Unknown market symbol '${order.market}'`;
        logException(new Error(message));
        return;
      }

      const notificationType = getOrderNotificationType(order);

      switch (notificationType) {
        case 'filled-market-order': {
          publishNotification({
            kind: notificationType,
            props: { order, market },
          });
          break;
        }
        case 'cancelled-market-order': {
          publishNotification({
            kind: notificationType,
            props: { order, market },
          });
          break;
        }
        case 'cancelled-limit-order': {
          publishNotification({
            kind: notificationType,
            props: { order, market },
          });

          break;
        }
        case 'submitted-order': {
          /**
           * processed based on REST API response
           */
          break;
        }
        case null:
          break;
        // no default
      }
    },
    [markets.entities, markets.ids.length],
  );

  useWSListener<OrderNotification>({
    channel,
    isReady,
    handleWSNotification,
  });
}

function useFillsListener(): void {
  const walletView = useWalletView();

  /**
   * Caches promisses to retrieve orders so that multiple fills don't
   * trigger many requests to the same order, even if they start before
   * the first attemt to retrieve the order is finished.
   */
  const orderPromiseCache = useMemo(
    () => new Cache<Promise<Order>>({ defaultTtl: ms.minutes(5) }),
    [],
  );

  const isReady = walletView.isSignedIn;
  const channel = 'fills.ALL';

  const { markets } = usePerpetualMarketsState();

  const [, fetchOrder] = useApi(getOrder);

  const retrieveOrder = useCallback(
    async (orderId: Order['id']): Promise<Order> => {
      const cachedPromise = orderPromiseCache.get(orderId);

      if (cachedPromise != null) {
        return cachedPromise;
      }

      const promise = new Promise<Order>((resolve, reject) => {
        fetchOrder({ id: orderId }, (resp) => {
          if (!resp.ok) {
            const message = `Error fetching order ${orderId} for fill notification`;
            reject(new Error(message, { cause: resp.error }));
            return;
          }
          const order = resp.data;
          resolve(order);
        });
      });

      orderPromiseCache.put(orderId, promise);
      return promise;
    },
    [fetchOrder, orderPromiseCache],
  );

  const handleWSNotification = useCallback(
    (notif: FillNotification) => {
      const fill = notif.data;

      (async function work() {
        const order = await retrieveOrder(fill.order_id);
        if (OrderFn.isMarketOrder(order)) return;

        if (markets.ids.length === 0) return; // markets not loaded yet

        const market = markets.entities[fill.market];

        if (market == null) {
          const message = `Unknown market symbol '${fill.market}'`;
          logException(new Error(message));
          return;
        }

        publishNotification({
          kind: 'filled-market-fill',
          props: { fill, market },
        });
      })().catch((err) => {
        const message = `Error retrieving order ${fill.order_id} for fill notification`;
        const error = new Error(message, { cause: err });
        logException(error);
      });
    },
    [markets.entities, markets.ids.length, retrieveOrder],
  );

  useWSListener<FillNotification>({
    channel,
    isReady,
    handleWSNotification,
  });
}
